/**
 * Delivery-failure accounting for fire-and-forget telemetry (#4022).
 *
 * THE DEFECT THIS CLOSES. `usageTracking.trackEvent` ended with
 * `.catch(() => {}); // fire and forget`. Every failed `logUsageEvent` POST was
 * discarded with no counter, no log, and no report -- so analytics loss was
 * indistinguishable from "nobody used the feature". The one time it WAS noticed,
 * it was only because a different error channel happened to catch it.
 *
 * Fire-and-forget is the right shape for telemetry: a page must never block or
 * break because analytics is down. What is NOT right is being unable to tell the
 * difference between "delivered" and "silently dropped". This module keeps the
 * non-blocking behaviour and makes the drops COUNTABLE.
 *
 * Deliberately dependency-free (see buildIdentity.ts for why shared code must
 * not add bare specifiers).
 */

export interface DeliveryStats {
  /** Events handed to the transport. */
  readonly attempted: number;
  /** Events the transport rejected. */
  readonly failed: number;
  /** Message from the most recent failure, or null. */
  readonly lastError: string | null;
  /** Epoch ms of the most recent failure, or null. */
  readonly lastFailureAt: number | null;
}

let attempted = 0;
let failed = 0;
let lastError: string | null = null;
let lastFailureAt: number | null = null;

type FailureListener = (stats: DeliveryStats) => void;
const listeners = new Set<FailureListener>();

function snapshot(): DeliveryStats {
  return { attempted, failed, lastError, lastFailureAt };
}

/** Record that an event was handed to the transport. */
export function recordAttempt(): void {
  attempted += 1;
}

/**
 * Record a delivery failure. Never throws: a reporting path that can throw would
 * reintroduce the very risk fire-and-forget exists to avoid.
 */
export function recordFailure(error: unknown): void {
  failed += 1;
  lastFailureAt = Date.now();
  try {
    lastError = error instanceof Error ? error.message : String(error);
  } catch {
    lastError = 'unserializable error';
  }
  listeners.forEach((fn) => {
    try {
      fn(snapshot());
    } catch {
      /* a listener must not be able to break telemetry */
    }
  });
}

/** Current counters. */
export function getDeliveryStats(): DeliveryStats {
  return snapshot();
}

/**
 * Subscribe to delivery failures — the hook an error reporter uses to surface
 * sustained telemetry loss. Emits on every failure, not on every attempt.
 * Returns an unsubscribe function.
 */
export function onDeliveryFailure(callback: FailureListener): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

/** Reset counters. Tests only; a no-op in production builds. */
export function __resetDeliveryStatsForTests(): void {
  if (process.env.NODE_ENV === 'production') return;
  attempted = 0;
  failed = 0;
  lastError = null;
  lastFailureAt = null;
  listeners.clear();
}
